import { IonFab, IonFabButton, IonInput, IonItem, IonLabel } from '@ionic/react';
import {
  PencilSimpleIcon,
  PlusIcon,
  TrashSimpleIcon,
  WalletIcon,
} from '@phosphor-icons/react';
import React, { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import { useSVFs } from '../../../context/SVFsContext';
import { useSiniestros } from '../../../context/SiniestrosContext';
import {
  ComprobanteItem,
  useComprobanteManager,
} from '../../../hooks/Reintegro/Comprobante/useComprobanteManager';
import { useAlert } from '../../../hooks/UI/useAlert';
import useCameraFilePicker from '../../../hooks/Utils/useCameraFilePicker';
import { iconSiniestroProps } from '../../../pages/siniestro';
import { capitalizeEveryWord } from '../../../utils/string';
import { validations } from '../../../utils/validations';
import { getVetFormIdFromSVFs } from '../../../utils/vetFormLink';
import Alert from '../../UI/Alert';
import Button from '../../UI/Button';
import CameraActionSheet from '../../UI/CameraActionSheet';
import FileUploader from '../../UI/FileUploader';
import FormHeader from '../../UI/FormHeader';
import FormItem from '../../UI/FormItem';
import Gallery from '../../UI/Gallery';
import Spinner from '../../UI/Spinner';
import './Comprobante.scss';
import { ComprobanteProps } from './types';

type FormValues = { concepto: string; monto: string };

type ComprobanteCardProps = {
  comprobante: ComprobanteItem;
  onViewFile: (fileId: string) => void;
  onEdit: () => void;
  onDelete: () => void;
  isVacuna?: boolean;
};

const ComprobanteCard: React.FC<ComprobanteCardProps> = ({
  comprobante,
  onViewFile,
  onEdit,
  onDelete,
  isVacuna,
}) => {
  const { status } = comprobante;
  const statusClass = status === 'error' ? `comprobante-card--${status}` : '';
  const isDisabled = status === 'submitting' || status === 'success';

  return (
    <div className={`comprobante-card ${statusClass}`}>
      <div className="comprobante-card__header">
        <h4 className="comprobante-card__title">
          {isVacuna ? 'Comprobante' : comprobante.concepto}
        </h4>
        <div className="comprobante-card__actions">
          {!isVacuna && (
            <button
              className={`comprobante-card__icon-btn ${isDisabled ? 'comprobante-card__icon-btn--disabled' : ''}`}
              onClick={!isDisabled ? onEdit : undefined}
              disabled={isDisabled}
            >
              <PencilSimpleIcon size={18} weight="bold" />
            </button>
          )}
          <button
            className={`comprobante-card__icon-btn ${isDisabled ? 'comprobante-card__icon-btn--disabled' : ''}`}
            onClick={!isDisabled ? onDelete : undefined}
            disabled={isDisabled}
          >
            <TrashSimpleIcon size={18} weight="bold" />
          </button>
        </div>
      </div>

      {!isVacuna && (
        <div className="comprobante-card__body">
          <div className="comprobante-card__info">
            <span className="comprobante-card__label">Monto</span>
            <span className="comprobante-card__value">${comprobante.monto}</span>
          </div>
        </div>
      )}

      <div className="comprobante-card__files">
        {comprobante.files.map((file, index) => (
          <FileUploader
            key={file.id}
            fileData={{
              label: `Archivo ${index + 1}`,
              uploaded: [file.preview],
              counter: 1,
              canViewFile: true,
              showUploadAction: false,
            }}
            onShowCameraOptions={() => undefined}
            onViewFile={() => onViewFile(file.id)}
          />
        ))}
      </div>
    </div>
  );
};

const Comprobante: React.FC<ComprobanteProps> = ({
  onSuccess,
  onClose,
  onComplete,
  manager: externalManager,
  isVacuna,
  mutateVacunaComprobantes,
}) => {
  const { currentSiniestro } = useSiniestros();
  const { SVFs, mutateComprobantesByVetFormId, vetFormId_Ref } = useSVFs();
  const { alertState, showAlert, hideAlert } = useAlert();
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const internalManager = useComprobanteManager(isVacuna);

  const manager = externalManager ?? internalManager;

  const { handleSubmit, control, reset, watch, errors } = useForm<FormValues>({
    defaultValues: { concepto: '', monto: '' },
    mode: 'onChange',
  });

  const concepto = watch('concepto');
  const monto = watch('monto');

  const cameraPicker = useCameraFilePicker({
    onFileSelected: manager.handleFileSelected,
  });

  const handleEdit = (id: string) => {
    const data = manager.startEdit(id);
    if (data) {
      reset({
        concepto: data.concepto,
        monto: data.monto,
      });
    }
  };

  const handleOpenNewForm = () => {
    reset({ concepto: '', monto: '' });
    manager.openForm();
  };

  const confirmDelete = (onConfirm: () => void) => {
    showAlert({
      header: 'Eliminar archivo',
      message: '¿Estás seguro de que querés eliminar este archivo?',
      buttons: [
        { text: 'Cancelar', role: 'cancel' },
        { text: 'Eliminar', role: 'destructive', handler: onConfirm },
      ],
    });
  };

  const handleDeleteFromGallery = () => {
    confirmDelete(() => manager.deleteFromGallery());
  };

  const handleDeleteComprobante = (id: string) => {
    confirmDelete(() => manager.removeComprobante(id));
  };

  const showCloseButton = !isProcessing && !isSubmitted;

  const handleClose = () => {
    if (manager.hasComprobantes || manager.hasTempFiles) {
      showAlert({
        header: 'Cancelar',
        message: 'Si cancelás ahora, los documentos agregados se perderán.',
        buttons: [
          { text: 'Volver', role: 'cancel' },
          { text: 'Entendido', handler: onClose },
        ],
      });
    } else {
      onClose();
    }
  };

  const onConfirmComprobante = ({ concepto, monto }: FormValues) => {
    if (!manager.hasTempFiles) return;

    const montoNormalizado = monto?.replace(',', '.');

    if (isVacuna) {
      manager.addComprobante('', '');
    } else if (manager.editingId) {
      manager.updateComprobante(manager.editingId, concepto, montoNormalizado);
    } else {
      manager.addComprobante(concepto, montoNormalizado);
    }
    reset();
  };

  const handleSubmitAll = async () => {
    if (isProcessing) return;
    setIsProcessing(true);

    const submitId = isVacuna
      ? String(currentSiniestro.id)
      : String(vetFormId_Ref.current ?? getVetFormIdFromSVFs(SVFs, currentSiniestro.id));

    console.log('[Comprobante] submitAll context', {
      isVacuna,
      currentSiniestro,
      vetFormIdRef: vetFormId_Ref.current,
      SVFs,
      submitId,
      pendingComprobantes: manager.pendingComprobantes,
    });

    const results = await manager.submitAllComprobantes(submitId);

    console.log('[Comprobante] submitAll results', results);

    if (results.allSuccess) {
      if (isVacuna) {
        await mutateVacunaComprobantes?.();
      } else {
        await mutateComprobantesByVetFormId(Number(submitId));
      }
      setIsProcessing(false);
      setIsSubmitted(true);
      onComplete?.();
      showAlert({
        header: 'Envio exitoso',
        message: 'Los comprobantes se enviaron correctamente.',
        buttons: [{ text: 'Entendido', handler: () => onSuccess?.() }],
      });
    } else {
      setIsProcessing(false);
      showAlert({
        header: 'Error al enviar',
        message:
          'Algunos comprobantes no pudieron enviarse. Revisá los marcados con error e intentá nuevamente.',
        buttons: [{ text: 'Entendido' }],
      });
    }
  };

  const pendingCount = manager.pendingComprobantes.length;

  const headerConfig = useMemo(() => {
    if (isSubmitted) {
      return {
        title: 'Comprobantes enviados',
        subtitle: 'Los comprobantes se enviaron correctamente',
      };
    }

    if (!manager.showForm && (pendingCount > 0 || isProcessing)) {
      return {
        title: 'Comprobantes listos',
        subtitle: isProcessing
          ? 'Enviando comprobantes...'
          : `Tenés ${pendingCount} comprobante${pendingCount > 1 ? 's' : ''} listo${pendingCount > 1 ? 's' : ''} para enviar`,
      };
    }

    if (manager.editingId) {
      return {
        title: 'Editar comprobante',
        subtitle: 'Modificá los datos del comprobante',
      };
    }

    return {
      title: 'Comprobante de pago',
      subtitle: 'Agregá un comprobante de pago',
    };
  }, [manager.showForm, manager.editingId, pendingCount, isProcessing, isSubmitted]);

  return (
    <div className="comprobante ion-padding">
      <FormHeader
        icon={<WalletIcon {...iconSiniestroProps} />}
        title={headerConfig.title}
        subtitle={headerConfig.subtitle}
      />

      {!manager.showForm && manager.groups.length > 0 && (
        <div className="comprobante__list">
          {manager.groups.map((c) => (
            <ComprobanteCard
              key={c.id}
              comprobante={c}
              isVacuna={isVacuna}
              onViewFile={(fileId) => manager.viewComprobante(c.id, fileId)}
              onEdit={() => handleEdit(c.id)}
              onDelete={() => handleDeleteComprobante(c.id)}
            />
          ))}
        </div>
      )}

      {manager.showForm && (
        <form
          onSubmit={handleSubmit(onConfirmComprobante)}
          className={isVacuna ? 'comprobante__form--vacuna' : undefined}
        >
          {!isVacuna && (
            <>
              <FormItem
                name="concepto"
                control={control}
                errors={errors}
                rules={{
                  required: 'El concepto es requerido',
                  pattern: validations.concepto,
                }}
                render={({ onChange, value }) => (
                  <IonItem>
                    <IonLabel position="floating">Concepto</IonLabel>
                    <IonInput
                      value={value}
                      onIonChange={onChange}
                      onIonBlur={() => {
                        if (value) {
                          onChange(capitalizeEveryWord(value));
                        }
                      }}
                      placeholder="Ej: Consulta veterinaria"
                    />
                  </IonItem>
                )}
              />

              <FormItem
                name="monto"
                control={control}
                errors={errors}
                rules={{
                  required: 'El monto es requerido',
                  pattern: validations.monto,
                }}
                render={({ onChange, value }) => (
                  <IonItem>
                    <IonLabel position="floating">Monto</IonLabel>
                    <IonInput
                      value={value ? `$ ${value}` : ''}
                      onIonInput={(e) => {
                        const inputValue = (e.target as HTMLIonInputElement)
                          .value as string;
                        const cleanValue = inputValue.replace('$', '').trim();
                        onChange(cleanValue);
                      }}
                      inputMode="decimal"
                      placeholder="$ 0.00"
                    />
                  </IonItem>
                )}
              />
            </>
          )}

          {manager.tempFiles.map((tempFile, index) => (
            <FileUploader
              key={tempFile.id}
              fileData={{
                label: `Comprobante ${index + 1}`,
                uploaded: [tempFile.preview],
                counter: 1,
                showUploadAction: false,
              }}
              onShowCameraOptions={cameraPicker.openCameraOptions}
              onViewFile={() => manager.viewTempFile(tempFile.id)}
            />
          ))}

          <FileUploader
            fileData={{
              label: manager.hasTempFiles ? 'otro Comprobante' : 'Comprobante',
              disabled: !isVacuna && (!concepto || !monto),
              counter: 0,
              showUploadedState: false,
            }}
            onShowCameraOptions={cameraPicker.openCameraOptions}
            onFileChange={cameraPicker.handlePDFChange}
            onViewFile={manager.viewTempFile}
            inputRefSetter={(el) => (cameraPicker.fileInputRef.current = el)}
          />

          <div className={isVacuna ? 'ion-padding-top' : undefined}>
            <Button type="submit" disabled={!manager.hasTempFiles}>
              {manager.editingId ? 'Guardar cambios' : 'Confirmar'}
            </Button>

            {manager.hasComprobantes && (
              <Button
                fill="clear"
                onClick={() => {
                  reset();
                  manager.clearTempFile();
                  manager.closeForm();
                }}
              >
                Cancelar
              </Button>
            )}

            {!manager.hasComprobantes && (
              <Button fill="clear" onClick={handleClose}>
                Cerrar
              </Button>
            )}
          </div>
        </form>
      )}

      {!manager.showForm && !isSubmitted && (pendingCount > 0 || isProcessing) && (
        <Button noMarginTop onClick={handleSubmitAll} disabled={isProcessing}>
          {isProcessing ? (
            <Spinner color="light" />
          ) : (
            `Enviar ${pendingCount > 1 ? 'comprobantes' : ''}`
          )}
        </Button>
      )}

      {showCloseButton && !manager.showForm && (
        <Button fill="clear" onClick={handleClose}>
          Cancelar
        </Button>
      )}

      {cameraPicker.showCameraOptions && (
        <CameraActionSheet
          showCameraOptions
          setShowCameraOptions={(v) =>
            v ? cameraPicker.openCameraOptions() : cameraPicker.closeCameraOptions()
          }
          takePhoto={cameraPicker.handleCamera}
          selectFromGallery={cameraPicker.handleGallery}
          uploadPDF={cameraPicker.handlePDFClick}
        />
      )}

      {manager.galleryState.isOpen && manager.galleryState.fileUrl && (
        <Gallery
          isOpen
          onClose={manager.closeGallery}
          fileUrl={manager.galleryState.fileUrl}
          mimeType={manager.galleryState.mimeType}
          title={
            !manager.galleryState.comprobanteId ? 'Archivo seleccionado' : 'Comprobante'
          }
          allowDelete={manager.canDeleteFromGallery}
          onDelete={handleDeleteFromGallery}
        />
      )}

      {manager.hasComprobantes && !manager.showForm && (
        <IonFab vertical="bottom" horizontal="end" slot="fixed">
          <IonFabButton onClick={handleOpenNewForm}>
            <PlusIcon size={24} weight="bold" />
          </IonFabButton>
        </IonFab>
      )}

      <Alert {...alertState} onDidDismiss={hideAlert} />
    </div>
  );
};

export default Comprobante;
